/**
 * This Store is mainly used for the Side Panel component, notes.
 * See the folder @/components/ui/side-panel/notes for usability.
 * Additionally, the SystimeHighlight component uses this.
 */

import Vue from 'vue';
import makeStoreDebugAccessible from '../util/storeDebug';
import {
  NotesClickedFrom,
  NotesMode
} from '@/components/ui/side-panel/notes/NotesMode';
import { NoteHighlight } from '@/types/highlightContract';

export interface IHighlightMarker {
  highlight: NoteHighlight;
  coordinates: {
    top: number;
    siblingsWithSameTopPosition: number;
  };
  // When a marker is temporary, it means there is no NoteHighlight (persistent) attached
  isTemp: boolean;
  cId: string;
}

export interface ICoordinates {
  x: number;
  y: number;
  bottom: number;
  top: number;
}

export interface HlObj {
  selection: Selection;
  range: Range;
  selectionCId: string | null;
  concatinatedTextSelection: string;
  ranges: Range[];
}

interface NotesState {
  notesMode: NotesMode;
  updatedEdit: boolean;
  highlightMarkers: Array<IHighlightMarker>;
  contextMenuActive: boolean;
  contextMenuCoordinates: ICoordinates;
  userCanCreateHighlight: boolean;
  temporarySelectionState: HlObj | null;
  showPageNotesOnly: boolean;
  notesClickedFrom: NotesClickedFrom;
}

const state: NotesState = Vue.observable({
  notesMode: NotesMode.VIEW,
  notesClickedFrom: NotesClickedFrom.PREVIEW,
  updatedEdit: false,
  highlightMarkers: [],
  contextMenuActive: false,
  contextMenuCoordinates: { x: 0, y: 0, bottom: 0, top: 0 },
  userCanCreateHighlight: false,
  temporarySelectionState: null,
  showPageNotesOnly: false
});

class NotesStore {
  constructor() {
    if (process.env.NODE_ENV !== 'production') {
      makeStoreDebugAccessible('notes-store', state);
    }
  }

  public get notesClickedFrom(): NotesClickedFrom {
    return state.notesClickedFrom;
  }

  public set notesClickedFrom(val: NotesClickedFrom) {
    state.notesClickedFrom = val;
  }

  public get mode(): NotesMode {
    return state.notesMode;
  }

  public get temporarySelectionState(): HlObj | null {
    return state.temporarySelectionState;
  }

  // This is used to only handle the temporary selection state
  public set temporarySelectionState(hlObj: HlObj | null) {
    state.temporarySelectionState = hlObj;
  }

  public get updatedEdit(): boolean {
    return state.updatedEdit;
  }

  public get contextMenuActive(): boolean {
    return state.contextMenuActive;
  }

  public set contextMenuActive(val: boolean) {
    state.contextMenuActive = val;
  }

  public get highlightMarkers(): Array<IHighlightMarker> {
    return state.highlightMarkers;
  }

  public get contextMenuCoordinates(): ICoordinates {
    return state.contextMenuCoordinates;
  }

  public get userCanCreateHighlight(): boolean {
    return state.userCanCreateHighlight;
  }

  public set userCanCreateHighlight(val: boolean) {
    state.userCanCreateHighlight = val;
  }

  public get showPageNotesOnly(): boolean {
    return state.showPageNotesOnly;
  }

  public set showPageNotesOnly(change: boolean) {
    state.showPageNotesOnly = change;
  }

  public showContextMenu(event: any): void {
    const wrapper = document.querySelector('.v-main__wrap');

    if (!wrapper) {
      return;
    }

    state.contextMenuActive = false;

    // Allow onDestroyed to be called before initializing new preview
    setTimeout(() => {
      state.contextMenuCoordinates.x =
        event.clientX - wrapper.getBoundingClientRect().left;
      if (state.temporarySelectionState) {
        const rangeRect =
          state.temporarySelectionState.range.getBoundingClientRect();
        const offsetForButtons = 10;
        state.contextMenuCoordinates.y =
          rangeRect.bottom -
          wrapper.getBoundingClientRect().top +
          offsetForButtons;
      }
      state.contextMenuActive = true;
    }, 16);
  }

  public setNotesMode(mode: NotesMode): void {
    state.notesMode = mode;
    state.updatedEdit = true;

    setTimeout(() => {
      state.updatedEdit = false;
    });
  }

  public deleteTempHighlightMarker(id: string): void {
    state.highlightMarkers = state.highlightMarkers.filter(
      (hl: IHighlightMarker) => {
        return !hl.isTemp && hl.highlight.id !== id;
      }
    );
  }

  public deletePermanentHighlightMarker(highlight: NoteHighlight): void {
    state.highlightMarkers = state.highlightMarkers.filter(
      (hl: IHighlightMarker) => {
        return hl.highlight.id !== highlight.id;
      }
    );
  }

  public updateTempMarkerToPermanent(highlight: NoteHighlight): void {
    const tempHighlight = state.highlightMarkers.find(
      (hl) => hl.isTemp === true
    );
    if (tempHighlight && highlight) {
      tempHighlight.isTemp = false;
      tempHighlight.highlight = highlight;
    }
  }

  public addToHighlightMarkers(marker: IHighlightMarker): void {
    const markerExists = state.highlightMarkers.some(
      (hlm) => hlm.highlight.id === marker.highlight.id
    );
    if (!markerExists) {
      state.highlightMarkers.push(marker);
    }
  }

  public closeContextMenu(): void {
    state.contextMenuActive = false;
  }

  public setTemporarySelectionStateFromSelection(currentSelection: Selection) {
    if (currentSelection.isCollapsed) {
      return;
    }

    const range = currentSelection.getRangeAt(0).cloneRange();
    const ranges = [];
    for (let i = 0; i < currentSelection.rangeCount; i++) {
      ranges[i] = currentSelection.getRangeAt(i);
    }
    const currentSelectionCId = NotesStore.findCId(
      range.commonAncestorContainer
    );

    state.temporarySelectionState = {
      selection: currentSelection,
      range,
      selectionCId: currentSelectionCId,
      concatinatedTextSelection: currentSelection.toString(),
      ranges
    };
  }

  private static findCId(node: Node): string | null {
    if (!node) {
      return null;
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      const elem = node as Element;

      if (elem.id && elem.id.startsWith('c')) {
        const cIdSubstring = elem.id.substring(1);
        const cId = parseInt(cIdSubstring, 10);

        if (!isNaN(cId)) {
          return elem.id;
        }
      }
    }

    if (node.parentNode) {
      return this.findCId(node.parentNode);
    }

    return null;
  }
}

export default new NotesStore();
